import React, { useEffect, useState } from 'react';
import './ProjectDisplay.css';
import { VscUnverified } from "react-icons/vsc";
import { VscVerifiedFilled } from "react-icons/vsc";
import bsc from '../../assets/bsc.png';
import eth from '../../assets/eth.png';
import sol from '../../assets/sol.png';
import arbitrum from '../../assets/arbitrum.svg';
import base from '../../assets/base.svg';
import polygon from '../../assets/polygon.svg';
import avax from '../../assets/avax.webp';
import pepu from '../../assets/pepu.webp';
import robin from '../../assets/robin.svg';
import UnlistedLogo from '../../assets/unlisted-logo.png';
import { AiOutlinePushpin } from "react-icons/ai";

const Pin = AiOutlinePushpin as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const Verified = VscVerifiedFilled as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const Unverified = VscUnverified as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

interface ProjectInterface {
  verified?: string;
  image?: string;
  chain?: string;
  title?: string;
  symbol?: string;
  countdown?: string;
  address?: string;
  banner?: string;
  square?: string;
  isad?: boolean;
  change?: boolean;
  url?: string;
}

const ProjectDisplay: React.FC<ProjectInterface> = ({
  verified,
  image,
  chain,
  title,
  symbol,
  countdown,
  address,
  banner,
  square,
  isad,
  change,
  url
}) => {
  const chainImages: { [key: string]: string } = {
    bsc: bsc,
    eth: eth,
    sol: sol,
    solana: sol,
    arbitrum: arbitrum,
    base: base,
    polygon: polygon,
    avax: avax,
    pepu: pepu,
    robin: robin,
  };

  const [chainId, setChainId] = useState<any>(null);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (!chain) return;
    const lowerChain = chain.toLowerCase();
    if (lowerChain === 'bsc') {
      setChainId(56);
    } else if (lowerChain === 'eth') {
      setChainId(1);
    } else if (lowerChain === 'sol' || lowerChain === 'solana') {
      setChainId(501424);
    } else if (lowerChain === 'base') {
      setChainId(8453);
    } else if (lowerChain === 'arbitrum') {
      setChainId(42161);
    } else if (lowerChain === 'polygon') {
      setChainId(137);
    } else if (lowerChain === 'avax') {
      setChainId(43114);
    } else if (lowerChain === 'robin') {
      setChainId(4663);
    } else {
      setChainId(null);
    }
  }, [chain]);

  const chainLogo = chain
    ? chainImages[chain.toLowerCase()] || UnlistedLogo
    : UnlistedLogo;

  const renderPriceChange = (change: string | number) => {
    const value = parseFloat(change as string);
    if (value > 999){
      return (
        <div className={`redgreen green`}>
          &gt;999%
        </div>
      );
    }
    const formattedValue = value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const changeClass = value >= 0 ? 'green' : 'red';
    if (formattedValue === "NaN" || !formattedValue) { return }
    return (
      <div className={`redgreen ${changeClass}`}>
        {formattedValue}%
      </div>
    );
  };

  useEffect(() => {
    if (address === null || chainId === null || !change) return;
    const fetchData = async () => {
      try {
        const response = await fetch(`https://api.dexview.com/pair?baseToken=${address}&chainId=${chainId}`, {
            headers: {
              "accept": "application/json, text/plain, */*",
              "accept-language": "nl-NL,nl;q=0.9,en-US;q=0.8,en;q=0.7",
              "if-none-match": "W/\"2244-8e+vWX7JgZT7/hBnItO2lL1ZTqo\"",
              "priority": "u=1, i",
              "sec-ch-ua": "\"Not(A:Brand\";v=\"99\", \"Google Chrome\";v=\"133\", \"Chromium\";v=\"133\"",
              "sec-ch-ua-mobile": "?0",
              "sec-ch-ua-platform": "\"Windows\"",
              "sec-fetch-dest": "empty",
              "sec-fetch-mode": "cors",
              "sec-fetch-site": "same-site",
              "secret": "5ff3a258-2700-11ed-a261-0242ac120002"
            },
            referrerPolicy: "same-origin",
            body: null,
            method: "GET",
            mode: "cors",
            credentials: "omit"
        });
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        const result = await response.json();
        setData(result.data);
      } catch (error: any) {
        console.error(error);
      }
    };
    fetchData();

    const interval = setInterval(fetchData, 1000);
    return () => clearInterval(interval);
  }, [chainId, address]);

  return (
    <a className='project-link' target={url ? '_blank' : '_self'} href={url || `/${chain}/${address}`}>
      {banner && (verified === "true" || verified === "yes") && (
        <img src={banner} className='display-banner'></img>
      )}
      <div className={`project-box ${isad ? 'pinned-box' : ''}`}>
        <div className="left-box">
          <div className="verified-box">
            {verified === "true" || verified === "yes"  ? (
              <Verified className='verified-badge' />
            ) : (
              <Unverified className='unverified-badge' />
            )}
          </div>
          <div className="image-box">
            <img
              className='image-project'
              src={image === "" ? UnlistedLogo : image}
              alt="Project"
            />
            <img
              className='chain-image'
              src={chainLogo}
              alt="Chain Logo"
            />
          </div>
          <div className="symbolandtitle">
            <div className="title-box">
              <div className="title-inbox">
                {title}
              </div> 
              {change && data && (
                renderPriceChange(data.newInformation?.priceChange24h)
              )}
            </div>
            <div className="symbol-box">{symbol}</div>
          </div>
        </div>
        <div className="right-box">
          <div className="time-box">
            {isad ? (
              <div className='pin-promoted'><Pin /> AD</div>
            ) : countdown}  
          </div>
        </div>
      </div>
    </a>
  );
};

export default ProjectDisplay;
